import { Typography } from "@mui/material";
import type { CredentialResponse } from "@react-oauth/google";
import { GoogleLogin } from "@react-oauth/google";
import { Button, Card, Checkbox, Col, Divider, Flex, Form, Input, Row } from "antd";
import { jwtDecode } from "jwt-decode";
import type React from "react";
import { useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import axiosInstance from "../../api/axiosInstance";
import HippaLogo from "../../assets/bg/hippa.png";
import LoadingModal from "../../components/LoadingModal";
import { useAppDispatch } from "../../hooks/redux-hooks";
import useUserInfo from "../../hooks/useUserInfo";
import { ensureCsrfToken, patchUserInfo } from "../../service/auth.service";
import { setIsDemoPath } from "../../slices/appStateSlice";
import { register } from "../../slices/authSlice";
import { fetchUserData } from "../../slices/userSlice";
import { EventType, identifyUser, trackEvent, trackServerEvent } from "../../utils/analytics";
import MarketingCopy from "./MarketingCopy";
import styles from "./auth.module.scss";

interface UtmParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
}

export interface DecodedGoogleCredential {
  email: string;
  name: string;
  picture: string;
}

const Register: React.FC = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const location = useLocation();
  const componentName = "login";
  const dispatch = useAppDispatch();
  const { fetchUserInfo } = useUserInfo();
  const [isLoading, setIsLoading] = useState(false);
  const [invitationDetails, setInvitationDetails] = useState<{
    email?: string;
    group_name?: string;
    inviter_name?: string;
  } | null>(null);
  const [utmParams, setUtmParams] = useState<UtmParams>({});
  const [affiliateCode, setAffiliateCode] = useState<string>("");

  const [groupInviteDetails, setGroupInviteDetails] = useState<{
    group_name?: string;
  } | null>(null);

  // Get invitation key and group invite token from URL query params
  const searchParams = new URLSearchParams(location.search);
  const invitationKey = searchParams.get("invitation");
  const groupInviteToken = searchParams.get("group_invite");

  useEffect(() => {
    ensureCsrfToken().catch(() => {
      // Cookie will be set by response; registration may fail with CSRF error if backend requires it
    });
  }, []);

  useEffect(() => {
    if (invitationKey) {
      // Validate invitation and get details
      axiosInstance
        .get(`/users/check-invitation/?key=${invitationKey}`)
        .then((response) => {
          setInvitationDetails(response.data);
          // Pre-fill email field if it matches invitation
          form.setFieldsValue({ email: response.data.email });
        })
        .catch((error) => {
          if (error.response?.status === 404) {
            Swal.fire({
              title: "Invalid Invitation",
              icon: "error",
              text: "This invitation link is invalid or has expired.",
            });
            navigate("/register");
          }
        });
    }
  }, [invitationKey, form, navigate]);

  useEffect(() => {
    if (groupInviteToken && !invitationKey) {
      axiosInstance
        .get(`/users/check-group-invite/?token=${groupInviteToken}`)
        .then((response) => {
          setGroupInviteDetails(response.data);
        })
        .catch(() => {
          Swal.fire({
            title: "Invalid Invite Link",
            icon: "error",
            text: "This invite link is invalid.",
          });
          navigate("/register");
        });
    }
  }, [groupInviteToken, invitationKey, navigate]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const params: UtmParams = {};

    const utmKeys = ["source", "medium", "campaign", "term", "content"];
    for (const param of utmKeys) {
      const value = searchParams.get(`utm_${param}`);
      if (value) {
        params[`utm_${param}` as keyof UtmParams] = value;
      }
    }

    // Check for affiliate/coupon code
    const code = searchParams.get("code");
    if (code) {
      setAffiliateCode(code);
    }

    setUtmParams(params);
  }, [location]);

  const handleRegister = async () => {
    setIsLoading(true);

    const { name, email, password } = form.getFieldsValue();

    try {
      const registrationData: any = invitationKey
        ? { name, email, password, invitation: invitationKey }
        : groupInviteToken
          ? { name, email, password, group_invite: groupInviteToken }
          : { name, email, password };

      // Add coupon code if present
      if (affiliateCode) {
        registrationData.coupon_code = affiliateCode;
      }

      const registerationSuccessful = await dispatch(register(registrationData)).unwrap();

      if (registerationSuccessful) {
        dispatch(setIsDemoPath(false));
        identifyUser(email, { email });
        window?.fbq("track", "CompleteRegistration");
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: "signup_complete", method: "email" });
        trackEvent(EventType.REGISTER, { email });
        trackServerEvent(EventType.REGISTER, { email });
        dispatch(fetchUserData());
        const token = localStorage.getItem("token");
        if (!token) {
          setIsLoading(false);
          return;
        }
        const joinedGroupName = invitationDetails?.group_name || groupInviteDetails?.group_name;
        if (joinedGroupName) {
          Swal.fire({
            title: "Welcome!",
            icon: "success",
            text: `Successfully registered and joined ${joinedGroupName}`,
          });
        }
        if (utmParams) {
          await patchUserInfo({ utm_data: utmParams });
        }
        await fetchUserInfo();
        navigate("/onboarding");
      }
    } catch (e) {
      console.error(e);
      Swal.fire({
        title: "Registration Failed",
        icon: "error",
        text: "There was an error during registration. Please try again.",
      });
    }

    setIsLoading(false);
  };

  const handleGoogleSignUp = async (credentialResponse: CredentialResponse) => {
    if (!credentialResponse.credential) return;

    const decoded: DecodedGoogleCredential = jwtDecode(credentialResponse.credential);

    try {
      const googleLoginData: any = {
        access_token: credentialResponse.credential,
        id_token: credentialResponse.credential,
        invitation: invitationKey,
        group_invite: groupInviteToken || undefined,
      };

      // Add coupon code if present
      if (affiliateCode) {
        googleLoginData.coupon_code = affiliateCode;
      }

      const response = await axiosInstance.post("/accounts/google/login/", googleLoginData);

      if (response.data.key) {
        localStorage.setItem("token", response.data.key);
        dispatch(setIsDemoPath(false));
        dispatch(fetchUserData());
        identifyUser(decoded.email, { email: decoded.email });
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: "signup_complete", method: "google" });
        trackEvent(EventType.GOOGLE_SIGN_UP, { email: decoded.email });
        trackServerEvent(EventType.GOOGLE_SIGN_UP, { email: decoded.email });
        const joinedGroupName = invitationDetails?.group_name || groupInviteDetails?.group_name;
        if (joinedGroupName) {
          Swal.fire({
            title: "Welcome!",
            icon: "success",
            text: `Successfully registered and joined ${joinedGroupName}`,
          });
        }
        if (utmParams) {
          await patchUserInfo({ utm_data: utmParams });
        }
        await fetchUserInfo();
        navigate("/onboarding");
      }
    } catch (error: any) {
      console.error("Google sign up error:", error);
      const detail = error?.response?.data?.detail;
      Swal.fire({
        title: "Sign Up Error",
        icon: "error",
        text:
          detail ||
          "It seems you might have an existing account. Please try signing in with your email or password instead. If you can't remember your password, you can use the 'Forgot Password' option.",
      });
    }
  };

  return (
    <>
      {isLoading && <LoadingModal />}
      <Row className="fullScreen auth-container">
        <Col xs={24} sm={24} md={12} lg={12} className={styles.marketingSection}>
          <MarketingCopy />
        </Col>
        <Col xs={24} sm={24} md={12} lg={12} className={styles.formSection}>
          <Card className={styles.formCard}>
            <Flex vertical justify="center" align="center">
              <Typography variant="h5" className={styles.formTitle}>
                {invitationDetails
                  ? `Join ${invitationDetails.group_name}`
                  : groupInviteDetails
                    ? `Join ${groupInviteDetails.group_name}`
                    : "Create Your Free Nudge Account"}
              </Typography>
              {!invitationDetails && !groupInviteDetails && (
                <>
                  <Typography className={styles.formSubtitle}>Get 5 free sessions with your trial.</Typography>
                  <Typography className={styles.formSubtitle}>No credit card required.</Typography>
                </>
              )}
              {invitationDetails && (
                <Typography className={styles.formSubtitle} style={{ marginBottom: 10 }}>
                  {invitationDetails.inviter_name} has invited you to join their team
                </Typography>
              )}
              {groupInviteDetails && !invitationDetails && (
                <Typography className={styles.formSubtitle} style={{ marginBottom: 10 }}>
                  Create an account to join the team
                </Typography>
              )}
              {!invitationDetails ? (
                <>
                  <div
                    style={{
                      marginTop: 10,
                      boxSizing: "border-box",
                    }}
                  >
                    <GoogleLogin
                      onSuccess={handleGoogleSignUp}
                      text="signup_with"
                      onError={() => {
                        console.error("Google Sign Up Failed");
                        Swal.fire({
                          title: "Sign Up Error",
                          icon: "error",
                          text: "It seems you might have an existing account. Please try signing in with your email or password instead. If you can't remember your password, you can use the 'Forgot Password' option.",
                        });
                      }}
                    />
                  </div>
                  <Divider style={{ width: "100%", margin: "10px 0px" }}>OR</Divider>
                </>
              ) : null}
              <Form
                className={styles.formContainer}
                form={form}
                layout="vertical"
                onFinish={handleRegister}
                autoComplete="off"
              >
                <Form.Item
                  name="name"
                  label="Full Name"
                  rules={[{ required: true, message: "Please enter your name." }]}
                >
                  <Input placeholder="Susan Johnson" />
                </Form.Item>
                <Form.Item
                  name="email"
                  label="Email"
                  rules={[
                    {
                      required: true,
                      message: "Please enter your email.",
                    },
                    {
                      pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                      message: "Please ensure a valid email address.",
                    },
                    {
                      validator: async (_, value) => {
                        if (invitationDetails && value !== invitationDetails.email) {
                          throw new Error(`Please use the invited email: ${invitationDetails.email}`);
                        }
                      },
                    },
                  ]}
                >
                  <Input placeholder="susan@example.com" disabled={!!invitationDetails} />
                </Form.Item>
                <Form.Item
                  name="password"
                  label="Password"
                  rules={[
                    { required: true, message: "Please enter your password." },
                    {
                      min: 5,
                      message: "Password must be at least 5 characters long",
                    },
                  ]}
                >
                  <Input.Password placeholder="Enter password" type="password" />
                </Form.Item>
                {affiliateCode && (
                  <Form.Item label="Promo Code">
                    <Input value={affiliateCode} disabled style={{ backgroundColor: "#f5f5f5" }} prefix="🎁" />
                  </Form.Item>
                )}
                <Form.Item
                  name="acceptTerms"
                  valuePropName="checked"
                  rules={[
                    {
                      validator: (_, value) =>
                        value
                          ? Promise.resolve()
                          : Promise.reject(new Error("You must accept the terms and conditions")),
                    },
                  ]}
                >
                  <Checkbox
                    style={{
                      fontSize: "12px",
                    }}
                  >
                    I agree to terms of Nudge's{" "}
                    <NavLink to={"/privacy"} target="_blank" rel="noopener noreferrer">
                      Privacy Policy
                    </NavLink>
                    ,{" "}
                    <NavLink to={"/terms"} target="_blank" rel="noopener noreferrer">
                      Terms of Service
                    </NavLink>{" "}
                    and{" "}
                    <a target="_blank" href="https://getnudgeai.com/baa" rel="noopener noreferrer">
                      Business Associate Agreement
                    </a>
                    .
                  </Checkbox>
                </Form.Item>
                <Form.Item>
                  <Button type="primary" htmlType="submit" style={{ width: "100%", marginTop: "10px" }}>
                    {invitationDetails || groupInviteDetails ? "Join Team" : "Create Free Account"}
                  </Button>
                </Form.Item>
              </Form>
              <Flex
                className={styles[`${componentName}__form-container-text`]}
                justify="center"
                style={{
                  marginTop: "10px",
                }}
              >
                Already a User? &nbsp;<NavLink to={`/login${location.search}`}>Login</NavLink>
              </Flex>
              <div className={styles[`${componentName}__form-container-bottom-notification`]}>
                Your data is protected with HIPAA-compliant encryption. <br /> Nudge removes any personally identifying
                data, including audio.
              </div>
              <img src={HippaLogo} alt="Hippa" className={styles[`${componentName}__side-image-hippa`]} />
            </Flex>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default Register;
